import React, { useEffect, useState } from 'react';
import { Check, X, Plus, ShieldCheck, KeyRound, Trash2, RefreshCw, Copy } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { authApi } from '../lib/authApi';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

const STATUS_TONE = {
  pending:  'bg-[#FBE3D6] text-[#7A2E0F] border-[#E8B79A]',
  approved: 'bg-[#E5F0EA] text-[#2D5F4F] border-[#4A7C5E]/30',
  rejected: 'bg-[#F0E7D2] text-[#7A857F] border-[#D4C9AF]',
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newInviteDays, setNewInviteDays] = useState(30);
  const [newInviteNotes, setNewInviteNotes] = useState('');
  const [toast, setToast] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([authApi.listUsers(statusFilter || null), authApi.listInvites()]);
      setUsers(u.items || []);
      setInvites(i.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [statusFilter]);

  const decide = async (user_id, decision) => {
    try { await authApi.decideUser(user_id, decision); await reload(); }
    catch (e) { setToast(e.response?.data?.detail || 'Action failed'); }
  };
  const createInvite = async () => {
    setCreating(true);
    try {
      const res = await authApi.createInvite({ expires_days: newInviteDays, notes: newInviteNotes || undefined });
      setToast(`Invite created: ${res.code}`);
      setNewInviteNotes('');
      await reload();
    } catch (e) { setToast(e.response?.data?.detail || 'Could not create invite'); }
    finally { setCreating(false); }
  };
  const revoke = async (code) => {
    if (!window.confirm(`Revoke invite ${code}?`)) return;
    try { await authApi.revokeInvite(code); await reload(); }
    catch (e) { setToast(e.response?.data?.detail || 'Revoke failed'); }
  };
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setToast(`Copied ${text}`); } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-12">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Admin · Users' }]} />
        <div className="mt-5 flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
              <ShieldCheck size={12} className="text-[#4A7C5E]" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">Admin</span>
            </div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight">User access</h1>
            <p className="mt-2 text-[13.5px] text-[#3A4A44] max-w-2xl">Review pending registrations, approve or reject accounts, and issue invite codes. Both password and Google sign-ins land here as <strong>pending</strong> until you approve them.</p>
          </div>
          <button onClick={reload} className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md border border-[#D4C9AF] bg-white text-[12.5px] font-medium hover:border-[#4A7C5E]" data-testid="admin-reload">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {toast && (
          <div className="mt-4 rounded-md bg-[#F5EFDD] border border-[#D4C9AF] px-3.5 py-2.5 text-[12.5px] text-[#3A4A44] flex items-center justify-between" data-testid="admin-toast">
            <span>{toast}</span>
            <button onClick={() => setToast(null)} className="text-[#7A857F] hover:text-[#1A2622]"><X size={13} /></button>
          </div>
        )}

        <section className="mt-8 rounded-xl bg-white border border-[#E5DCC4] p-5" data-testid="invites-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-lg font-semibold text-[#1A2622] flex items-center gap-2"><KeyRound size={15} className="text-[#A06628]" /> Invite codes</h2>
              <p className="mt-1 text-[12px] text-[#7A857F]">Share an invite code with someone before they can register.</p>
            </div>
            <div className="flex items-end gap-2">
              <label className="text-[11.5px] text-[#3A4A44]">
                <span className="block uppercase tracking-[0.14em] font-semibold">Days valid</span>
                <input type="number" min="1" max="365" value={newInviteDays} onChange={(e) => setNewInviteDays(parseInt(e.target.value) || 30)} className="mt-1 h-9 w-20 px-2 rounded-md border border-[#E5DCC4] text-[13px] bg-white outline-none focus:border-[#4A7C5E]" data-testid="invite-days" />
              </label>
              <label className="text-[11.5px] text-[#3A4A44]">
                <span className="block uppercase tracking-[0.14em] font-semibold">Note (optional)</span>
                <input type="text" value={newInviteNotes} onChange={(e) => setNewInviteNotes(e.target.value)} placeholder="e.g. ACME Health" className="mt-1 h-9 w-56 px-2 rounded-md border border-[#E5DCC4] text-[13px] bg-white outline-none focus:border-[#4A7C5E]" data-testid="invite-notes" />
              </label>
              <button onClick={createInvite} disabled={creating} className="h-9 px-3.5 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] font-medium hover:bg-[#2D5F4F] inline-flex items-center gap-1.5" data-testid="invite-create">
                <Plus size={13} /> {creating ? 'Creating…' : 'New invite'}
              </button>
            </div>
          </div>
          <div className="mt-4 -mx-5 overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[10.5px] uppercase tracking-wider text-[#7A857F] bg-[#F5EFDD]">
                <tr>
                  <th className="text-left px-5 py-2">Code</th>
                  <th className="text-left px-5 py-2">Notes</th>
                  <th className="text-left px-5 py-2">Expires</th>
                  <th className="text-left px-5 py-2">Used by</th>
                  <th className="text-right px-5 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-4 text-[12.5px] text-[#7A857F]">No invite codes yet.</td></tr>
                )}
                {invites.map((i) => (
                  <tr key={i.code} className="border-t border-[#F0E7D2]" data-testid={`invite-row-${i.code}`}>
                    <td className="px-5 py-2.5 font-mono text-[12.5px] text-[#1A2622]">{i.code}</td>
                    <td className="px-5 py-2.5 text-[#3A4A44]">{i.notes || '—'}</td>
                    <td className="px-5 py-2.5 text-[#3A4A44]">{fmtDate(i.expires_at)}</td>
                    <td className="px-5 py-2.5 text-[#3A4A44]">{i.used_by || <span className="text-[#7A857F]">unused</span>}</td>
                    <td className="px-5 py-2.5 text-right">
                      <button onClick={() => copy(i.code)} className="inline-flex items-center gap-1 text-[12px] text-[#4A7C5E] hover:text-[#2D5F4F] px-1.5"><Copy size={12} /> Copy</button>
                      {!i.used_by && (
                        <button onClick={() => revoke(i.code)} className="inline-flex items-center gap-1 text-[12px] text-[#A06628] hover:text-[#7A2E0F] px-1.5 ml-2"><Trash2 size={12} /> Revoke</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-8 rounded-xl bg-white border border-[#E5DCC4] p-5" data-testid="users-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-display text-lg font-semibold text-[#1A2622]">Users</h2>
              <p className="mt-1 text-[12px] text-[#7A857F]">{loading ? 'Loading…' : `${users.length} shown`}</p>
            </div>
            <div className="flex items-center gap-1.5">
              {['', 'pending', 'approved', 'rejected'].map((s) => (
                <button key={s || 'all'} onClick={() => setStatusFilter(s)}
                        className={`h-8 px-3 rounded-md text-[12px] font-medium border ${statusFilter === s ? 'bg-[#1A2622] text-[#FBF6EC] border-[#1A2622]' : 'bg-white text-[#3A4A44] border-[#D4C9AF] hover:border-[#4A7C5E]'}`}
                        data-testid={`users-filter-${s || 'all'}`}>{s || 'All'}</button>
              ))}
            </div>
          </div>
          <div className="mt-4 -mx-5 overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="text-[10.5px] uppercase tracking-wider text-[#7A857F] bg-[#F5EFDD]">
                <tr>
                  <th className="text-left px-5 py-2">Name</th>
                  <th className="text-left px-5 py-2">Email</th>
                  <th className="text-left px-5 py-2">Company</th>
                  <th className="text-left px-5 py-2">Provider</th>
                  <th className="text-left px-5 py-2">Status</th>
                  <th className="text-left px-5 py-2">Joined</th>
                  <th className="text-right px-5 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && !loading && (
                  <tr><td colSpan={7} className="px-5 py-4 text-[12.5px] text-[#7A857F]">No users match the filter.</td></tr>
                )}
                {users.map((u) => (
                  <tr key={u.user_id} className="border-t border-[#F0E7D2]" data-testid={`user-row-${u.user_id}`}>
                    <td className="px-5 py-2.5 text-[#1A2622] font-medium">{u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—'}</td>
                    <td className="px-5 py-2.5 text-[#3A4A44]">{u.email}</td>
                    <td className="px-5 py-2.5 text-[#3A4A44]">{u.company || '—'}</td>
                    <td className="px-5 py-2.5 text-[#3A4A44] capitalize">{u.auth_provider || 'password'}</td>
                    <td className="px-5 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] uppercase tracking-wider font-semibold border ${STATUS_TONE[u.status] || STATUS_TONE.pending}`}>{u.status || 'pending'}</span>
                    </td>
                    <td className="px-5 py-2.5 text-[#3A4A44]">{fmtDate(u.created_at)}</td>
                    <td className="px-5 py-2.5 text-right">
                      {u.status !== 'approved' && (
                        <button onClick={() => decide(u.user_id, 'approve')} className="inline-flex items-center gap-1 text-[12px] text-[#2D5F4F] hover:text-[#1A2622] px-1.5" data-testid={`user-approve-${u.user_id}`}><Check size={13} /> Approve</button>
                      )}
                      {u.status !== 'rejected' && u.role !== 'admin' && (
                        <button onClick={() => decide(u.user_id, 'reject')} className="inline-flex items-center gap-1 text-[12px] text-[#A06628] hover:text-[#7A2E0F] px-1.5 ml-2" data-testid={`user-reject-${u.user_id}`}><X size={13} /> Reject</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
